import { ChevronDown, Download, FileText, FileType2 } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { V3IconButton } from "@/components/ui/v3/button";
import { V3_MENU_CONTENT, V3_MENU_ITEM } from "@/components/ui/v3/menu";
import { toast } from "@/hooks/use-toast";
import { stripVerifyMarkers } from "@/lib/verifyMarkers";
import { useWhatsNewStore } from "@/store/whatsNew.store";
import type { NoteExportInput } from "./export/download";
import type { ExportFormat } from "./export/fileName";
import { loadNoteExport } from "./export/load";
import { usePhoneLayout } from "./layout";
import { useVerifyGate } from "./VerifyReview";

interface Props {
  document: NoteExportInput;
  disabled?: boolean;
  // v3.2 phones: PDF and Word as items of the visit's "…" menu (once the
  // note's actions are in the top bar) instead of their own menu.
  asMenuItems?: boolean;
}

export const NoteExportMenu = ({
  document,
  disabled = false,
  asMenuItems = false,
}: Props): JSX.Element => {
  const { t, i18n } = useTranslation();
  // Phones: an icon button in the one-row toolbar, so Copy keeps its width.
  const compact = usePhoneLayout();
  const [working, setWorking] = useState<ExportFormat | null>(null);
  const hasText = !!document.markdown.trim();
  const [menuOpen, setMenuOpen] = useState(false);
  const gate = useVerifyGate();
  // "Try it" from What's new opens this menu once the note is there.
  const whatsNewIntent = useWhatsNewStore((state) => state.intent);
  const ready = !disabled && hasText && !working;
  useEffect(() => {
    if (asMenuItems || !ready || !whatsNewIntent) return;
    if (useWhatsNewStore.getState().takeIntent("export")) {
      void loadNoteExport().catch(() => undefined);
      setMenuOpen(true);
    }
  }, [asMenuItems, ready, whatsNewIntent]);

  // The file never carries a "[verify]" marker: they are resolved in the
  // review step first (and stripped here as a last guard).
  const run = async (format: ExportFormat, markdown: string): Promise<void> => {
    setWorking(format);
    try {
      const { exportNote, UnsupportedScriptError } = await loadNoteExport();
      try {
        await exportNote(
          { ...document, markdown: stripVerifyMarkers(markdown) },
          format,
          {
            locale: i18n.language,
            labels: {
              visitDate: t("visit.workspace.export.visitDate"),
              page: t("visit.workspace.export.page", {
                page: "{{page}}",
                total: "{{total}}",
              }),
            },
          },
        );
      } catch (error) {
        toast({
          variant: "destructive",
          description:
            error instanceof UnsupportedScriptError
              ? t("visit.workspace.export.unsupportedScript")
              : t("visit.workspace.export.failed"),
        });
      }
    } catch {
      // The export code could not be loaded (offline, new deploy).
      toast({
        variant: "destructive",
        description: t("visit.workspace.export.failed"),
      });
    } finally {
      setWorking(null);
    }
  };

  const choose = (format: ExportFormat): void => {
    if (!gate) {
      void run(format, document.markdown);
      return;
    }
    // After the menu has closed, so its focus handling does not fight the
    // review dialog.
    window.setTimeout(() => {
      gate.request({
        reason: "export",
        label:
          format === "pdf"
            ? t("visit.workspace.verify.review.exportPdf")
            : t("visit.workspace.verify.review.exportDocx"),
        proceed: (text) => void run(format, text),
      });
    }, 0);
  };

  const label = working
    ? t("visit.workspace.export.working")
    : t("visit.workspace.export.button");
  const itemClass = AppConfig.enableAppRedesign
    ? V3_MENU_ITEM
    : "min-h-11 gap-3 px-3 text-sm lg:min-h-9";

  const items = (
    <>
      <DropdownMenuItem
        className={itemClass}
        disabled={asMenuItems && !ready}
        onSelect={() => {
          choose("pdf");
        }}
      >
        <FileText aria-hidden />
        {t("visit.workspace.export.pdf")}
      </DropdownMenuItem>
      <DropdownMenuItem
        className={itemClass}
        disabled={asMenuItems && !ready}
        onSelect={() => {
          choose("docx");
        }}
      >
        <FileType2 aria-hidden />
        {t("visit.workspace.export.docx")}
      </DropdownMenuItem>
    </>
  );
  if (asMenuItems) return items;

  return (
    <DropdownMenu
      open={menuOpen}
      onOpenChange={(open) => {
        setMenuOpen(open);
        // Fetch the export code while the provider picks a format.
        if (open) void loadNoteExport().catch(() => undefined);
      }}
    >
      <DropdownMenuTrigger asChild>
        {AppConfig.enableAppRedesign ? (
          <V3IconButton
            aria-busy={!!working}
            data-testid="note-export-menu"
            disabled={disabled || !hasText || !!working}
            label={label}
          >
            {working ? <LoadingSpinner /> : <Download aria-hidden />}
          </V3IconButton>
        ) : (
          <Button
            aria-busy={!!working}
            aria-label={compact ? label : undefined}
            className={compact ? "w-11 shrink-0 px-0" : "gap-1.5"}
            data-testid="note-export-menu"
            disabled={disabled || !hasText || !!working}
            title={compact ? label : undefined}
            type="button"
            variant="outline"
          >
            {working ? <LoadingSpinner /> : <Download aria-hidden />}
            {!compact && (
              <>
                {t("visit.workspace.export.button")}
                <ChevronDown aria-hidden className="opacity-70" />
              </>
            )}
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={
          AppConfig.enableAppRedesign
            ? `${V3_MENU_CONTENT} min-w-60`
            : "min-w-60"
        }
      >
        {items}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
